import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { billingIncidentsAPI, serviceDeliveryAPI } from '../lib/api';
import {
  Plus, Loader2, Edit, Trash2, Receipt, AlertCircle, Filter, X,
} from 'lucide-react';

export const TIPO_OPTIONS = [
  { value: 'no_cobra', label: 'No se cobra este mes' },
  { value: 'parcial', label: 'Cobro parcial' },
  { value: 'extra', label: 'Trabajo extra (se cobra aparte)' },
  { value: 'descuento', label: 'Descuento' },
  { value: 'otro', label: 'Otro' },
];

export const ESTADO_OPTIONS = [
  { value: 'pendiente', label: 'Pendiente de facturar' },
  { value: 'facturado', label: 'Facturado' },
  { value: 'no_factura', label: 'No se factura' },
];

const tipoLabel = (v) => TIPO_OPTIONS.find((o) => o.value === v)?.label || v;
const estadoLabel = (v) => ESTADO_OPTIONS.find((o) => o.value === v)?.label || v;

const tipoColor = {
  no_cobra: 'bg-red-100 text-red-700',
  parcial: 'bg-amber-100 text-amber-700',
  extra: 'bg-emerald-100 text-emerald-700',
  descuento: 'bg-blue-100 text-blue-700',
  otro: 'bg-gray-100 text-gray-700',
};

const estadoColor = {
  pendiente: 'bg-amber-100 text-amber-700 border-amber-200',
  facturado: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  no_factura: 'bg-gray-100 text-gray-600 border-gray-200',
};

const fmtMonth = (m) => {
  if (!m) return '—';
  const [y, mm] = m.split('-');
  const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  const idx = parseInt(mm, 10) - 1;
  return `${meses[idx] || mm} ${y}`;
};

const currentMonth = () => new Date().toISOString().slice(0, 7);

const emptyDraft = (clientId) => ({
  client_id: clientId || '',
  month: currentMonth(),
  tipo: 'no_cobra',
  importe: '',
  descripcion: '',
  estado: 'pendiente',
});

// clientId: si se pasa, muestra solo las incidencias de ESE cliente (vista ficha).
// Si no, muestra la vista global (Finanzas) con filtros, resumen y selector de cliente.
export default function BillingIncidentsSection({ clientId = null, clientName = '' }) {
  const isGlobal = !clientId;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(emptyDraft(clientId));
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [filterMonth, setFilterMonth] = useState('');
  const [filterEstado, setFilterEstado] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = clientId ? { client_id: clientId } : {};
      if (isGlobal && filterMonth) params.month = filterMonth;
      if (isGlobal && filterEstado && filterEstado !== 'all') params.estado = filterEstado;
      const res = await billingIncidentsAPI.list(params);
      setItems(res.items || []);
    } catch (e) {
      toast.error(e.message || 'Error al cargar incidencias');
    }
    setLoading(false);
  }, [clientId, isGlobal, filterMonth, filterEstado]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (isGlobal) {
      serviceDeliveryAPI.getClients('todos').then((cs) => setClients(cs || [])).catch(() => {});
    }
  }, [isGlobal]);

  const openNew = () => {
    setEditing(null);
    setDraft(emptyDraft(clientId));
    setDialogOpen(true);
  };

  const openEdit = (it) => {
    setEditing(it);
    setDraft({
      client_id: it.client_id || '',
      month: it.month || currentMonth(),
      tipo: it.tipo || 'no_cobra',
      importe: it.importe != null ? String(it.importe) : '',
      descripcion: it.descripcion || '',
      estado: it.estado || 'pendiente',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!draft.client_id) { toast.error('Selecciona un cliente'); return; }
    if (!draft.month) { toast.error('Indica el mes'); return; }
    setSaving(true);
    try {
      const payload = {
        client_id: draft.client_id,
        month: draft.month,
        tipo: draft.tipo,
        importe: draft.importe === '' || draft.importe == null ? null : parseFloat(draft.importe),
        descripcion: draft.descripcion || '',
        estado: draft.estado,
      };
      if (editing) {
        await billingIncidentsAPI.update(editing.id, payload);
        toast.success('Incidencia actualizada');
      } else {
        await billingIncidentsAPI.create(payload);
        toast.success('Incidencia registrada');
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(e.message || 'Error al guardar');
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta incidencia? Esta acción no se puede deshacer.')) return;
    setDeletingId(id);
    try {
      await billingIncidentsAPI.delete(id);
      toast.success('Incidencia eliminada');
      await load();
    } catch (e) {
      toast.error(e.message || 'Error al eliminar');
    }
    setDeletingId(null);
  };

  // Resumen del mes actual (solo vista global)
  const summary = useMemo(() => {
    if (!isGlobal) return null;
    const cm = currentMonth();
    const delMes = items.filter((i) => i.month === cm);
    const pendientes = delMes.filter((i) => i.estado === 'pendiente');
    const extras = delMes.filter((i) => i.tipo === 'extra');
    const descuentos = delMes.filter((i) => i.tipo === 'descuento');
    const totalExtra = extras.reduce((s, i) => s + (i.importe || 0), 0);
    const totalDescuento = descuentos.reduce((s, i) => s + (i.importe || 0), 0);
    return { cm, total: delMes.length, pendientes: pendientes.length, extras: extras.length, descuentos: descuentos.length, totalExtra, totalDescuento };
  }, [items, isGlobal]);

  const clearFilters = () => { setFilterMonth(''); setFilterEstado('all'); };

  return (
    <div className="space-y-6" data-testid="billing-incidents-section">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Receipt className="w-5 h-5 text-emerald-600" />
            Incidencias de Cobro
          </h3>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Excepciones de facturación de {isGlobal ? 'todos los clientes' : (clientName || 'este cliente')} (mes a mes).
            Es solo un registro interno para facturar a mano en Quipu — <strong>no modifica Quipu automáticamente</strong>.
          </p>
        </div>
        <Button onClick={openNew} data-testid="add-billing-incident-btn" className="shrink-0">
          <Plus className="w-4 h-4 mr-2" /> Nueva incidencia
        </Button>
      </div>

      {/* Resumen mes actual (global) */}
      {isGlobal && summary && (
        <Card data-testid="billing-summary" className="border-emerald-100 bg-emerald-50/40">
          <CardContent className="py-4">
            <div className="flex items-center gap-2 mb-3 text-sm font-medium text-emerald-800">
              <AlertCircle className="w-4 h-4" /> Resumen de {fmtMonth(summary.cm)}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div><div className="text-2xl font-bold">{summary.total}</div><div className="text-xs text-muted-foreground">incidencias este mes</div></div>
              <div><div className="text-2xl font-bold text-amber-600">{summary.pendientes}</div><div className="text-xs text-muted-foreground">pendientes de facturar</div></div>
              <div><div className="text-2xl font-bold text-emerald-600">{summary.totalExtra.toFixed(0)}€</div><div className="text-xs text-muted-foreground">{summary.extras} trabajo(s) extra</div></div>
              <div><div className="text-2xl font-bold text-blue-600">{summary.totalDescuento.toFixed(0)}€</div><div className="text-xs text-muted-foreground">{summary.descuentos} descuento(s)</div></div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filtros (global) */}
      {isGlobal && (
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Filter className="w-4 h-4" /> Filtrar:
          </div>
          <Input
            type="month"
            value={filterMonth}
            onChange={(e) => setFilterMonth(e.target.value)}
            className="w-40"
            data-testid="filter-month"
          />
          <Select value={filterEstado} onValueChange={setFilterEstado}>
            <SelectTrigger className="w-52" data-testid="filter-estado"><SelectValue placeholder="Estado" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              {ESTADO_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {(filterMonth || filterEstado !== 'all') && (
            <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
              <X className="w-4 h-4 mr-1" /> Limpiar
            </Button>
          )}
        </div>
      )}

      {/* Lista */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Cargando...
        </div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Receipt className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">No hay incidencias de cobro registradas.</p>
            <p className="text-xs">Haz clic en "Nueva incidencia" para registrar una excepción de facturación.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="billing-incidents-list">
          {items.map((it) => (
            <Card key={it.id} data-testid={`billing-incident-${it.id}`} className="hover:shadow-sm transition-shadow">
              <CardContent className="py-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      {isGlobal && <span className="font-semibold">{it.client_name}</span>}
                      <Badge className={tipoColor[it.tipo] || 'bg-gray-100 text-gray-700'} variant="secondary">{tipoLabel(it.tipo)}</Badge>
                      <Badge variant="outline" className="font-normal">{fmtMonth(it.month)}</Badge>
                      {it.importe != null && (
                        <span className="text-sm font-medium text-foreground">{it.importe.toFixed(2)}€</span>
                      )}
                    </div>
                    {it.descripcion && <p className="text-sm text-muted-foreground">{it.descripcion}</p>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant="outline" className={estadoColor[it.estado] || ''}>{estadoLabel(it.estado)}</Badge>
                    <Button variant="ghost" size="icon" onClick={() => openEdit(it)} data-testid={`edit-incident-${it.id}`}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(it.id)} disabled={deletingId === it.id} data-testid={`delete-incident-${it.id}`}>
                      {deletingId === it.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4 text-red-500" />}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog crear/editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="billing-incident-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar incidencia' : 'Nueva incidencia de cobro'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {isGlobal && (
              <div>
                <Label>Cliente</Label>
                <Select value={draft.client_id} onValueChange={(v) => setDraft({ ...draft, client_id: v })}>
                  <SelectTrigger data-testid="incident-client-select"><SelectValue placeholder="Selecciona cliente" /></SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => {
                      const cid = c._id || c.id;
                      return <SelectItem key={cid} value={cid}>{c.nombre || c.name}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Mes afectado</Label>
                <Input type="month" value={draft.month} onChange={(e) => setDraft({ ...draft, month: e.target.value })} data-testid="incident-month" />
              </div>
              <div>
                <Label>Importe (€) <span className="text-muted-foreground text-xs">opcional</span></Label>
                <Input type="number" step="0.01" placeholder="—" value={draft.importe} onChange={(e) => setDraft({ ...draft, importe: e.target.value })} data-testid="incident-importe" />
              </div>
            </div>
            <div>
              <Label>Tipo</Label>
              <Select value={draft.tipo} onValueChange={(v) => setDraft({ ...draft, tipo: v })}>
                <SelectTrigger data-testid="incident-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIPO_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Descripción / motivo</Label>
              <Textarea
                value={draft.descripcion}
                onChange={(e) => setDraft({ ...draft, descripcion: e.target.value })}
                placeholder="Ej: el cliente paró en agosto por vacaciones, reel extra para campaña de rebajas..."
                rows={3}
                data-testid="incident-descripcion"
              />
            </div>
            <div>
              <Label>Estado</Label>
              <Select value={draft.estado} onValueChange={(v) => setDraft({ ...draft, estado: v })}>
                <SelectTrigger data-testid="incident-estado"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ESTADO_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-incident-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editing ? 'Guardar cambios' : 'Registrar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
